import { Script, LockingScript, OP, Utils } from '@bsv/sdk';
/**
 * BitCom template for handling multi-protocol Bitcoin transactions
 *
 * BitCom allows multiple protocols to be embedded in a single transaction output
 * using the pipe ('|') delimiter to separate different protocol sections.
 *
 * This implementation mirrors the Go template structure and behavior.
 */
export default class BitCom {
    protocols;
    scriptPrefix;
    constructor(protocols = [], scriptPrefix = []) {
        this.protocols = protocols;
        this.scriptPrefix = scriptPrefix;
    }
    /**
     * Decodes a script to extract BitCom protocol data
     *
     * Mirrors the Go implementation's decode algorithm exactly.
     *
     * @param script - The script to decode
     * @returns BitComDecoded - The decoded BitCom structure, or null if no OP_RETURN found
     */
    static decode(script) {
        // Handle null script safely - return empty structure like Go
        if (script == null) {
            return {
                protocols: [],
                scriptPrefix: []
            };
        }
        let scriptBytes;
        if (script instanceof LockingScript) {
            scriptBytes = script.toBinary();
        }
        else {
            scriptBytes = script.toBinary();
        }
        const pos = this.findReturn(scriptBytes, 0);
        if (pos === -1) {
            return null;
        }
        // Get prefix if present (matches Go: prefix = (*scr)[:pos])
        let prefix = [];
        if (pos > 0) {
            prefix = scriptBytes.slice(0, pos);
        }
        const bitcom = {
            scriptPrefix: prefix,
            protocols: []
        };
        // Start after OP_RETURN (matches Go: pos++)
        const currentPos = pos + 1;
        // Parse protocols using SDK's built-in parsing (no custom readOp functions)
        const remainingScript = Script.fromBinary(scriptBytes.slice(currentPos));
        const chunks = remainingScript.chunks;
        let chunkIndex = 0;
        let currentProtocolPos = currentPos;
        while (chunkIndex < chunks.length) {
            const protocol = {
                protocol: '',
                script: [],
                pos: currentProtocolPos
            };
            // Read protocol identifier from current chunk
            const protocolChunk = chunks[chunkIndex];
            if (protocolChunk.data == null) {
                break;
            }
            protocol.protocol = Utils.toUTF8(protocolChunk.data);
            chunkIndex++;
            // Collect script chunks until we find a pipe delimiter or reach end
            const protocolChunks = [];
            while (chunkIndex < chunks.length) {
                const chunk = chunks[chunkIndex];
                // Check if this chunk is a pipe delimiter
                if (chunk.data != null && chunk.data.length === 1 && chunk.data[0] === 0x7c) {
                    // Found pipe delimiter - end current protocol
                    chunkIndex++; // Skip the pipe
                    break;
                }
                // Add chunk to current protocol (preserve chunk structure)
                protocolChunks.push(chunk);
                chunkIndex++;
            }
            // Convert chunks back to binary for protocol.script
            const protocolScript = new Script(protocolChunks);
            protocol.script = protocolScript.toBinary();
            bitcom.protocols.push(protocol);
            // Update position for next protocol
            currentProtocolPos += protocolChunk.op + 1 + protocol.script.length + (chunkIndex < chunks.length ? 2 : 0); // +2 for pipe delimiter
        }
        return bitcom;
    }
    /**
     * Creates a locking script from the BitCom structure
     *
     * Mirrors the Go Lock() method exactly.
     *
     * @returns LockingScript - The BitCom locking script
     */
    lock() {
        const script = new Script();
        // Add prefix if present (matches Go: s := script.NewFromBytes(b.ScriptPrefix))
        if (this.scriptPrefix.length > 0) {
            script.writeBin(this.scriptPrefix);
        }
        if (this.protocols.length > 0) {
            // Add OP_RETURN (matches Go: _ = s.AppendOpcodes(script.OpRETURN))
            script.writeOpCode(OP.OP_RETURN);
            for (let i = 0; i < this.protocols.length; i++) {
                const protocol = this.protocols[i];
                // Add protocol identifier as push data (matches Go: _ = s.AppendPushData([]byte(p.Protocol)))
                const protocolBytes = Utils.toArray(protocol.protocol, 'utf8');
                script.writeBin(protocolBytes);
                // Add protocol script data directly (matches Go: s = script.NewFromBytes(append(*s, p.Script...)))
                if (protocol.script.length > 0) {
                    // Parse the protocol script to get its chunks and append them
                    const protocolScript = Script.fromBinary(protocol.script);
                    for (const chunk of protocolScript.chunks) {
                        if (chunk.data != null) {
                            script.writeBin(chunk.data);
                        }
                        else {
                            script.writeOpCode(chunk.op);
                        }
                    }
                }
                // Add pipe delimiter if not the last protocol (matches Go: _ = s.AppendPushData([]byte("|")))
                if (i < this.protocols.length - 1) {
                    const pipeBytes = Utils.toArray('|', 'utf8');
                    script.writeBin(pipeBytes);
                }
            }
        }
        return new LockingScript(script.chunks);
    }
    /**
     * Finds the position of OP_RETURN in the script
     *
     * Mirrors the Go findReturn function exactly.
     *
     * @param scriptBytes - The script bytes to search
     * @param from - Starting position
     * @returns number - Position of OP_RETURN, or -1 if not found
     */
    static findReturn(scriptBytes, from) {
        for (let i = from; i < scriptBytes.length; i++) {
            if (scriptBytes[i] === OP.OP_RETURN) {
                return i;
            }
        }
        return -1;
    }
    /**
     * Finds the position of pipe delimiter in the script
     *
     * Mirrors the Go findPipe function exactly.
     *
     * @param scriptBytes - The script bytes to search
     * @param from - Starting position
     * @returns number - Position of pipe delimiter, or -1 if not found
     */
    // Custom readOp methods removed - using SDK's built-in parsing instead
    /**
     * Converts various input types to a Script object
     *
     * Mirrors the Go ToScript function exactly.
     *
     * @param data - The data to convert (Script, LockingScript, or byte array)
     * @returns Script - The converted script, or null if conversion fails
     */
    static toScript(data) {
        if (data instanceof Script) {
            return data;
        }
        if (data instanceof LockingScript) {
            return new Script(data.chunks);
        }
        if (Array.isArray(data)) {
            if (data.length === 0) {
                return new Script();
            }
            return Script.fromBinary(data);
        }
        return null;
    }
}
//# sourceMappingURL=BitCom.js.map